/**
 * The web editor's image node (#1106): Tiptap's `Image` with a node view that
 * paints `sitescope-attachment://` and message refs from the preview map
 * rather than letting the browser GET them. Moved out of
 * `RichTextEditor.web.tsx` so `webEditorExtensions.ts` can build the
 * production extension list without the whole editor module behind it.
 *
 * The node is an atom so a click becomes a node selection (ProseMirror only
 * does that for atoms) and Delete / Backspace can remove it. The stock image
 * node is a plain leaf, so a click dropped a caret beside the picture and the
 * editor's focus rule hid any selection ring.
 *
 * Hover and selection paint a softened red frame over the picture, so the
 * picture does not move. Delete sits inside that frame, in the top-right
 * corner, and shows on hover or while the picture is selected. A picture
 * shorter than the chip draws the chip above the frame.
 * Delete and Backspace ask the host before the picture is taken out of the
 * field, including when the caret is against the picture or a selection
 * contains it. With no host confirmer, they delete at once.
 */
import Image from '@tiptap/extension-image';

import {
  pendingInlineImagePreviewSrc,
  SS_ATTACHMENT_SRC_ATTR,
} from '../../services/pendingInlineImagePreview';
import { colors } from '../../theme/colors';
import { attachmentIdFromInlineSrc } from '../../utils/richContent/inlineImageRef';
import { isBrowserSafeInlineImageSrc } from '../../utils/richContent/localNetworkImgSrc';
import { displaySrcForInlineImage } from '../richHtmlContentShared';
import {
  CLICK_TO_EXPAND_LABEL,
  placeClickToExpandCaption,
} from './inlineImageExpand';

import type { Editor } from '@tiptap/core';
import type { NodeType, ResolvedPos } from '@tiptap/pm/model';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { Step } from '@tiptap/pm/transform';

const REMOVE_IMAGE_ATTR = 'data-ss-image-remove';
/** Matches the frame stroke so Delete starts on the inner edge of the border. */
const FRAME_BORDER_PX = 4;
/**
 * Same hue as `colors.error`, held back so a 4px stroke reads as a frame
 * and not a solid validation outline.
 */
const FRAME_BORDER_ALPHA = 0.6;

function frameBorderColor(hex: string): string {
  const raw = hex.replace('#', '');
  const red = Number.parseInt(raw.slice(0, 2), 16);
  const green = Number.parseInt(raw.slice(2, 4), 16);
  const blue = Number.parseInt(raw.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${FRAME_BORDER_ALPHA})`;
}

const removeConfirmers = new WeakMap<Editor, (pos: number) => void>();

type PendingImageRemove = {
  src: string;
  pos: number;
  /** The picture was removed. The position may now sit on the next node. */
  gone: boolean;
  off: () => void;
};

const pendingRemoves = new WeakMap<Editor, PendingImageRemove>();

/**
 * Delete stays on the inner edge of the frame when the picture can hold it.
 * A shorter picture draws the chip above the frame so the border does not
 * cut it off.
 */
export function inlineImageDeleteChipPlacement(
  imageHeight: number,
  chipHeight: number,
): 'inside' | 'outside' {
  if (imageHeight > 0 && chipHeight > 0 && imageHeight < chipHeight) {
    return 'outside';
  }
  return 'inside';
}

function releasePendingImageRemove(editor: Editor): void {
  const pending = pendingRemoves.get(editor);
  if (!pending) {
    return;
  }
  pending.off();
  pendingRemoves.delete(editor);
}

function findCutBefore($pos: ResolvedPos): ResolvedPos | null {
  if (!$pos.parent.type.spec.isolating) {
    for (let depth = $pos.depth - 1; depth >= 0; depth -= 1) {
      if ($pos.index(depth) > 0) {
        return $pos.doc.resolve($pos.before(depth + 1));
      }
      if ($pos.node(depth).type.spec.isolating) {
        break;
      }
    }
  }
  return null;
}

function findCutAfter($pos: ResolvedPos): ResolvedPos | null {
  if (!$pos.parent.type.spec.isolating) {
    for (let depth = $pos.depth - 1; depth >= 0; depth -= 1) {
      const parent = $pos.node(depth);
      if ($pos.index(depth) + 1 < parent.childCount) {
        return $pos.doc.resolve($pos.after(depth + 1));
      }
      if (parent.type.spec.isolating) {
        break;
      }
    }
  }
  return null;
}

function imagePosInSelection(
  state: EditorState,
  imageType: NodeType,
): number | null {
  let found: number | null = null;
  state.doc.nodesBetween(
    state.selection.from,
    state.selection.to,
    (node, pos) => {
      if (found != null) {
        return false;
      }
      if (node.type === imageType) {
        found = pos;
        return false;
      }
      return true;
    },
  );
  return found;
}

/**
 * Position of the picture ProseMirror would remove for this key.
 * `direction` -1 is Backspace, 1 is Delete. Empty textblocks are left alone:
 * the stock keymap selects the picture there instead of deleting it.
 */
function imagePosRemovedByKey(
  state: EditorState,
  imageType: NodeType,
  direction: -1 | 1,
): number | null {
  const { selection } = state;
  if (!selection.empty) {
    return imagePosInSelection(state, imageType);
  }
  const $cursor = selection.$from;
  const adjacent = direction < 0 ? $cursor.nodeBefore : $cursor.nodeAfter;
  if (adjacent?.type === imageType) {
    return direction < 0 ? $cursor.pos - adjacent.nodeSize : $cursor.pos;
  }
  if (!$cursor.parent.isTextblock || $cursor.parent.content.size === 0) {
    return null;
  }
  const atEdge =
    direction < 0
      ? $cursor.parentOffset === 0
      : $cursor.parentOffset === $cursor.parent.content.size;
  if (!atEdge) {
    return null;
  }
  const $cut = direction < 0 ? findCutBefore($cursor) : findCutAfter($cursor);
  if (!$cut || $cut.depth !== $cursor.depth - 1) {
    return null;
  }
  const sibling = direction < 0 ? $cut.nodeBefore : $cut.nodeAfter;
  if (!sibling || sibling.type !== imageType || !sibling.isAtom) {
    return null;
  }
  return direction < 0 ? $cut.pos - sibling.nodeSize : $cut.pos;
}

/** The editor asks this before Delete or Backspace takes a picture out. */
export function setInlineImageRemoveConfirmer(
  editor: Editor,
  confirm: ((pos: number) => void) | null,
): void {
  if (confirm) {
    removeConfirmers.set(editor, confirm);
  } else {
    removeConfirmers.delete(editor);
  }
}

export function deleteInlineImageAt(editor: Editor, pos: number): boolean {
  if (editor.isDestroyed || !editor.isEditable) {
    return false;
  }
  const node = editor.state.doc.nodeAt(pos);
  if (!node || node.type.name !== 'image') {
    return false;
  }
  return editor.chain().setNodeSelection(pos).deleteSelection().focus().run();
}

/**
 * True when this step removes the content at `pos`, rather than replacing
 * that node in place. `mapResult().deleted` is also set for a same-size
 * src rewrite, so it cannot tell those apart.
 */
function stepClearsPosition(step: Step, pos: number): boolean {
  let cleared = false;
  step.getMap().forEach((oldStart, oldEnd, newStart, newEnd) => {
    if (cleared || pos < oldStart || pos >= oldEnd) {
      return;
    }
    if (newEnd - newStart === 0) {
      cleared = true;
    }
  });
  return cleared;
}

function followPendingImage(
  pending: PendingImageRemove,
  transaction: Transaction,
): void {
  if (pending.gone) {
    return;
  }
  let pos = pending.pos;
  for (const step of transaction.steps) {
    const mapped = step.getMap().mapResult(pos);
    if (mapped.deleted && stepClearsPosition(step, pos)) {
      pending.gone = true;
      pending.pos = mapped.pos;
      return;
    }
    pos = mapped.pos;
  }
  pending.pos = pos;
}

function requestInlineImageRemove(editor: Editor, pos: number): boolean {
  const confirm = removeConfirmers.get(editor);
  if (!confirm) {
    return false;
  }
  releasePendingImageRemove(editor);
  const src = String(editor.state.doc.nodeAt(pos)?.attrs.src ?? '');
  const pending: PendingImageRemove = {
    src,
    pos,
    gone: false,
    off: () => undefined,
  };
  const onTransaction = ({ transaction }: { transaction: Transaction }) => {
    followPendingImage(pending, transaction);
  };
  pending.off = () => editor.off('transaction', onTransaction);
  editor.on('transaction', onTransaction);
  pendingRemoves.set(editor, pending);
  confirm(pos);
  return true;
}

/** True while a delete dialog is still waiting on this editor. */
export function inlineImageRemoveStillPending(editor: Editor): boolean {
  return pendingRemoves.has(editor);
}

/**
 * Removes the picture the open dialog asked about.
 * A src rewrite keeps the dialog armed on that same picture and returns
 * false, so the first confirm does not delete it. A removed picture returns
 * false and drops the pending remove, leaving whatever node followed it.
 */
export function commitInlineImageRemove(editor: Editor): boolean {
  const pending = pendingRemoves.get(editor);
  if (!pending) {
    return false;
  }
  if (pending.gone || editor.isDestroyed || !editor.isEditable) {
    releasePendingImageRemove(editor);
    return false;
  }
  const node = editor.state.doc.nodeAt(pending.pos);
  if (!node || node.type.name !== 'image') {
    releasePendingImageRemove(editor);
    return false;
  }
  const src = String(node.attrs.src ?? '');
  if (src !== pending.src) {
    pending.src = src;
    return false;
  }
  const pos = pending.pos;
  releasePendingImageRemove(editor);
  return deleteInlineImageAt(editor, pos);
}

/** Drops a pending confirmation without changing the document. */
export function cancelInlineImageRemove(editor: Editor): void {
  releasePendingImageRemove(editor);
}

function isRemoveImageTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(`[${REMOVE_IMAGE_ATTR}]`) != null
  );
}

export const SiteScopeImage = Image.extend({
  atom: true,
  addKeyboardShortcuts() {
    const ask = (direction: -1 | 1): boolean => {
      if (!this.editor.isEditable || this.editor.isDestroyed) {
        return false;
      }
      const pos = imagePosRemovedByKey(this.editor.state, this.type, direction);
      if (pos == null) {
        return false;
      }
      return requestInlineImageRemove(this.editor, pos);
    };
    const backward = (): boolean => ask(-1);
    const forward = (): boolean => ask(1);
    // Same chords as TipTap's keymap, so a modified delete cannot skip the ask.
    return {
      Backspace: backward,
      'Mod-Backspace': backward,
      'Shift-Backspace': backward,
      'Ctrl-h': backward,
      'Alt-Backspace': backward,
      Delete: forward,
      'Mod-Delete': forward,
      'Ctrl-d': forward,
      'Ctrl-Alt-Backspace': forward,
      'Alt-Delete': forward,
      'Alt-d': forward,
    };
  },
  parseHTML() {
    return [
      {
        tag: `img[${SS_ATTACHMENT_SRC_ATTR}]`,
        getAttrs: (element: HTMLElement | string) => {
          if (typeof element === 'string') {
            return false;
          }
          const src = element.getAttribute(SS_ATTACHMENT_SRC_ATTR);
          if (!src) {
            return false;
          }
          return {
            src,
            alt: element.getAttribute('alt'),
            title: element.getAttribute('title'),
          };
        },
      },
      {
        tag: 'img[src]:not([src^="data:"])',
      },
    ];
  },
  addNodeView() {
    return ({ node, editor, getPos }) => {
      // Wrapper so ProseMirror can stamp node.attrs.src on `dom` without
      // making the browser GET `sitescope-attachment://…`.
      const wrap = document.createElement('span');
      wrap.className = 'ss-inline-image ss-image-expand-host';
      wrap.contentEditable = 'false';
      // The frame and Delete are overlays. A layout border would shove the
      // picture when the pointer enters.
      wrap.style.display = 'inline-block';
      wrap.style.position = 'relative';
      wrap.style.verticalAlign = 'bottom';
      wrap.style.maxWidth = '100%';
      wrap.style.lineHeight = '0';
      wrap.style.boxSizing = 'border-box';
      // Keep the frame and Delete inside this picture. Without a stacking
      // context their z-index competes with menus and other pictures.
      wrap.style.isolation = 'isolate';
      const img = document.createElement('img');
      img.draggable = false;
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.setAttribute(REMOVE_IMAGE_ATTR, '');
      remove.setAttribute('aria-label', 'Delete image');
      remove.style.display = 'none';
      remove.style.position = 'absolute';
      remove.style.zIndex = '2';
      remove.style.top = `${FRAME_BORDER_PX}px`;
      remove.style.right = `${FRAME_BORDER_PX}px`;
      remove.style.alignItems = 'center';
      remove.style.gap = '4px';
      remove.style.margin = '0';
      remove.style.padding = '4px 8px';
      remove.style.whiteSpace = 'nowrap';
      remove.style.border = 'none';
      remove.style.borderRadius = '0';
      remove.style.backgroundColor = colors.backgroundPrimary;
      remove.style.color = colors.textPrimary;
      remove.style.fontFamily = 'inherit';
      remove.style.fontSize = '12px';
      remove.style.fontWeight = '600';
      remove.style.lineHeight = '16px';
      remove.style.cursor = 'pointer';
      const icon = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg',
      );
      icon.setAttribute('width', '12');
      icon.setAttribute('height', '12');
      icon.setAttribute('viewBox', '0 0 12 12');
      icon.setAttribute('aria-hidden', 'true');
      icon.style.display = 'block';
      icon.style.flexShrink = '0';
      const iconPath = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'path',
      );
      iconPath.setAttribute(
        'd',
        'M1.75 3.25h8.5M4.6 3.25V2.1h2.8v1.15M2.7 3.25l.5 7.15h5.6l.5-7.15',
      );
      iconPath.setAttribute('stroke-linejoin', 'round');
      iconPath.setAttribute('fill', 'none');
      iconPath.setAttribute('stroke', 'currentColor');
      iconPath.setAttribute('stroke-width', '1.5');
      iconPath.setAttribute('stroke-linecap', 'round');
      icon.append(iconPath);
      const label = document.createElement('span');
      label.textContent = 'Delete';
      remove.append(icon, label);
      const frame = document.createElement('span');
      frame.setAttribute('data-ss-image-frame', '');
      frame.setAttribute('aria-hidden', 'true');
      frame.style.display = 'none';
      frame.style.position = 'absolute';
      frame.style.zIndex = '1';
      frame.style.top = '0';
      frame.style.right = '0';
      frame.style.bottom = '0';
      frame.style.left = '0';
      frame.style.boxSizing = 'border-box';
      frame.style.border = `${FRAME_BORDER_PX}px solid ${frameBorderColor(
        colors.error,
      )}`;
      frame.style.pointerEvents = 'none';
      const caption = document.createElement('span');
      caption.className = 'ss-image-expand';
      caption.setAttribute('aria-hidden', 'true');
      caption.textContent = CLICK_TO_EXPAND_LABEL;
      caption.style.position = 'absolute';
      caption.style.zIndex = '1';
      caption.style.pointerEvents = 'none';
      caption.style.whiteSpace = 'nowrap';
      caption.style.backgroundColor = colors.actionSolidBlack;
      caption.style.color = colors.textLight;
      caption.style.fontSize = '12px';
      caption.style.lineHeight = '16px';
      caption.style.padding = '4px 8px';
      caption.style.display = 'none';
      wrap.append(img, frame, caption, remove);

      const removeImage = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!editor.isEditable || editor.isDestroyed) {
          return;
        }
        const pos = getPos();
        if (typeof pos !== 'number') {
          return;
        }
        if (requestInlineImageRemove(editor, pos)) {
          return;
        }
        deleteInlineImageAt(editor, pos);
      };
      remove.addEventListener('mousedown', removeImage);
      remove.addEventListener('click', removeImage);

      let hovered = false;
      let selected = false;
      const placeDeleteChip = () => {
        const hidden = remove.style.display === 'none';
        if (hidden) {
          remove.style.visibility = 'hidden';
          remove.style.display = 'inline-flex';
        }
        const outside =
          inlineImageDeleteChipPlacement(
            img.offsetHeight,
            remove.offsetHeight,
          ) === 'outside';
        if (hidden) {
          remove.style.display = 'none';
          remove.style.visibility = '';
        }
        if (outside) {
          remove.style.top = 'auto';
          remove.style.bottom = '100%';
          remove.style.right = '0px';
          return;
        }
        remove.style.top = `${FRAME_BORDER_PX}px`;
        remove.style.right = `${FRAME_BORDER_PX}px`;
        remove.style.bottom = 'auto';
      };
      const syncChrome = () => {
        if (editor.isDestroyed) {
          return;
        }
        const show =
          (hovered || selected) && editor.isEditable && !editor.isDestroyed;
        img.style.outline = '';
        img.style.outlineOffset = '';
        img.style.boxShadow = '';
        wrap.style.border = '';
        frame.style.display = show ? 'block' : 'none';
        remove.style.display = show ? 'inline-flex' : 'none';
        if (show) {
          placeDeleteChip();
        }
      };
      const onImageLoad = () => {
        placeDeleteChip();
      };
      const onPointerEnter = () => {
        hovered = true;
        syncChrome();
      };
      const onPointerLeave = () => {
        hovered = false;
        syncChrome();
      };
      const onRemoveEnter = () => {
        remove.style.backgroundColor = colors.backgroundSecondary;
      };
      const onRemoveLeave = () => {
        remove.style.backgroundColor = colors.backgroundPrimary;
      };
      const followPointer = (event: MouseEvent) => {
        if (
          !wrap.classList.contains('ss-image-openable') ||
          wrap.closest('.ss-tiptap-has-image-open') == null
        ) {
          caption.style.display = 'none';
          return;
        }
        caption.style.display = 'inline-flex';
        placeClickToExpandCaption(wrap, caption, event.clientX, event.clientY);
      };
      const hideCaption = () => {
        caption.style.display = 'none';
      };
      wrap.addEventListener('mouseenter', onPointerEnter);
      wrap.addEventListener('mouseenter', followPointer);
      wrap.addEventListener('mousemove', followPointer);
      wrap.addEventListener('mouseleave', onPointerLeave);
      wrap.addEventListener('mouseleave', hideCaption);
      remove.addEventListener('mouseenter', onRemoveEnter);
      remove.addEventListener('mouseleave', onRemoveLeave);
      img.addEventListener('load', onImageLoad);
      editor.on('update', syncChrome);

      // Own the press. ProseMirror's mouseup otherwise drops a caret beside
      // the picture and clears the node selection this handler just set.
      const selectImage = (event: Event) => {
        if (!editor.isEditable || editor.isDestroyed) {
          return;
        }
        if (isRemoveImageTarget(event.target)) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        const pos = getPos();
        if (typeof pos !== 'number') {
          return;
        }
        editor.view.focus();
        editor.commands.setNodeSelection(pos);
      };
      wrap.addEventListener('mousedown', selectImage);

      let applyGeneration = 0;
      const apply = (src: string, alt: string) => {
        const generation = ++applyGeneration;
        img.alt = alt;
        const openable = attachmentIdFromInlineSrc(src) != null;
        wrap.classList.toggle('ss-image-openable', openable);
        if (!openable) {
          caption.style.display = 'none';
        }
        const cached = pendingInlineImagePreviewSrc(src);
        if (isBrowserSafeInlineImageSrc(cached)) {
          img.src = cached;
          if (img.complete) {
            placeDeleteChip();
          }
          return;
        }
        img.removeAttribute('src');
        displaySrcForInlineImage(src)
          .then(display => {
            if (generation !== applyGeneration || !display) {
              return;
            }
            if (!isBrowserSafeInlineImageSrc(display)) {
              return;
            }
            img.src = display;
            if (img.complete) {
              placeDeleteChip();
            }
          })
          .catch(() => undefined);
      };

      const showSelected = (next: boolean) => {
        selected = next;
        wrap.classList.toggle('ProseMirror-selectednode', next);
        syncChrome();
      };

      apply(String(node.attrs.src ?? ''), String(node.attrs.alt ?? ''));
      return {
        dom: wrap,
        // The preview src is applied after mount. Without this, that attribute
        // write makes ProseMirror rebuild the view and drop the selection.
        ignoreMutation: () => true,
        stopEvent: event => isRemoveImageTarget(event.target),
        selectNode: () => showSelected(true),
        deselectNode: () => showSelected(false),
        update: next => {
          if (next.type.name !== 'image') {
            return false;
          }
          apply(String(next.attrs.src ?? ''), String(next.attrs.alt ?? ''));
          return true;
        },
        destroy: () => {
          remove.removeEventListener('mousedown', removeImage);
          remove.removeEventListener('click', removeImage);
          wrap.removeEventListener('mousedown', selectImage);
          wrap.removeEventListener('mouseenter', onPointerEnter);
          wrap.removeEventListener('mouseenter', followPointer);
          wrap.removeEventListener('mousemove', followPointer);
          wrap.removeEventListener('mouseleave', onPointerLeave);
          wrap.removeEventListener('mouseleave', hideCaption);
          remove.removeEventListener('mouseenter', onRemoveEnter);
          remove.removeEventListener('mouseleave', onRemoveLeave);
          img.removeEventListener('load', onImageLoad);
          editor.off('update', syncChrome);
        },
      };
    };
  },
});
